// Pitch shifter that keeps speed: two read heads sweep through a delay line at `ratio` times real speed,
// each faded in and out with a sin² window half a window apart (the two windows always sum to 1).
// This is the classic granular / overlap-add delay-line shifter. Grains are resampled, so formants move
// with the pitch (a chipmunk going up, a giant going down). At 0 semitones it settles into a plain delay
// of half a window, so every chain has the same constant latency.
//
// Loaded as a Blob URL module, so it works in AudioContext and OfflineAudioContext alike.
export const PITCH_WINDOW = 0.05; // seconds

export const PITCH_WORKLET = `
const WINDOW = ${PITCH_WINDOW};
class VoicePitch extends AudioWorkletProcessor {
  static get parameterDescriptors() {
    return [{ name: "semitones", defaultValue: 0, minValue: -24, maxValue: 24, automationRate: "k-rate" }];
  }
  constructor() {
    super();
    this.W = Math.round(sampleRate * WINDOW);
    let n = 1;
    while (n < this.W * 2 + 8) n <<= 1;
    this.mask = n - 1;
    this.buf = new Float32Array(n);
    this.w = 0;
    this.p = 0; // phase of read head 1 through the window, 0..1
  }
  read(d) {
    const pos = this.w - d;
    const i = Math.floor(pos), f = pos - i;
    const a = this.buf[i & this.mask], b = this.buf[(i + 1) & this.mask];
    return a + (b - a) * f;
  }
  process(inputs, outputs, params) {
    const o = outputs[0] && outputs[0][0];
    if (!o) return true;
    const x = inputs[0] && inputs[0][0];
    const st = params.semitones[0];
    const W = this.W;
    const still = Math.abs(st) < 0.01;
    const step = (1 - Math.pow(2, st / 12)) / W;
    const settle = 0.25 / W;
    for (let n = 0; n < o.length; n++) {
      this.buf[this.w & this.mask] = x ? x[n] : 0;
      if (still) {
        // Glide to phase 0: head 1 silent, head 2 a fixed half-window behind. No comb filtering.
        if (this.p !== 0) {
          if (this.p < 0.5) this.p = Math.max(0, this.p - settle);
          else { this.p += settle; if (this.p >= 1) this.p = 0; }
        }
      } else {
        this.p += step;
        if (this.p >= 1) this.p -= 1;
        else if (this.p < 0) this.p += 1;
      }
      const p1 = this.p, p2 = p1 < 0.5 ? p1 + 0.5 : p1 - 0.5;
      const s = Math.sin(Math.PI * p1), g1 = s * s;
      o[n] = g1 * this.read(p1 * W) + (1 - g1) * this.read(p2 * W);
      this.w = (this.w + 1) & this.mask;
    }
    return true;
  }
}
registerProcessor("voice-pitch", VoicePitch);
`;
